/**
 * Preferências negativas ("não quero X", "sem X") como exclusões DURAS.
 *
 * Módulo puro e multilingue (pt/en/es/fr/it/de): não faz chamadas a IA nem a
 * Maps. Deteta pedidos explícitos para excluir uma categoria do roteiro
 * (ex.: "não quero restaurantes", "sem museus") e expõe utilitários para
 * filtrar sugestões/paragens já geradas.
 */

export type ExcludedCategoryKind =
  | "restaurant"
  | "cafe"
  | "bakery"
  | "museum"
  | "monument"
  | "church"
  | "park"
  | "nature"
  | "shopping"
  | "nightlife"
  | "market"
  | "hotel"
  | "viewpoint"
  | "beach"
  | "castle"
  | "zoo"
  | "thermal"
  | "library"
  | "winery"
  | "theme_park";

export type NegativePreferences = {
  /** Categorias que o utilizador pediu explicitamente para excluir. */
  excludedCategories: ExcludedCategoryKind[];
  /** Frases originais que originaram cada exclusão (para depuração/logs). */
  matchedPhrases: string[];
};

const fold = (value: string) =>
  (value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/gu, "")
    .toLowerCase();

/** Termos de categoria por idioma, reutilizados na negação. */
export const CATEGORY_TERMS: Record<ExcludedCategoryKind, string> = {
  restaurant:
    "restaurantes?|restaurants?|ristorant\\w*|tascas?|marisqueiras?|bistr[oô]s?|comida|jantar|almoco|almoço|dining|lunch|dinner|cenar|comer|essen|diner\\w*|osteria\\w*|trattoria\\w*",
  cafe: "cafes?|caf[eé]s?|coffee\\s*shops?|cafeteria\\w*|caffe\\w*|kaffee",
  bakery:
    "pastelarias?|padarias?|confeitarias?|baker(?:y|ies)|patisserie\\w*|boulangerie\\w*|panaderia\\w*|backerei",
  museum: "museus?|museums?|mus[eé]es?|museos?|musei|museo|cultur\\w*",
  monument: "monumentos?|monuments?",
  church: "igrejas?|catedrais?|cathedrals?|s[eé]s|churches?|eglises?|iglesias?|kirchen?",
  park: "parques?|jardins?|parks?|gardens?|jardines?|gaerten|jardins",
  nature: "natureza|trilhos?|hikes?|hiking|florestas?|nature|natur",
  shopping: "compras|lojas?|shoppings?|shops?|shopping|einkaufen|acheter|comprar",
  nightlife:
    "bares?|bars?|discotecas?|clubs?|nightlife|vida\\s+(?:noturna|nocturna|notturna)|noturna|nocturna|nachtleben|nightclub\\w*",
  market: "mercados?|markets?|mercato\\w*|marche\\w*|feiras?",
  hotel: "hot[eé]is|hotels?|hostels?|pousadas?|alojament\\w*",
  viewpoint: "miradour\\w+|viewpoints?|vistas?\\s+panor\\w+|panor[aâ]micas?|overlooks?",
  beach: "praias?|beach(?:es)?|plages?|playas?|spiaggi\\w*|strand\\w*|areal|banhos\\s+de\\s+mar",
  castle: "castel\\w*|castles?|fortalezas?|fortress(?:es)?|chateaux?|chateau|schloss|schloesser",
  zoo: "zoos?|jardins?\\s+zoologicos?|aquarios?|aquariums?|oceanarios?",
  thermal: "termas?|spas?|thermal\\w*|banhos?\\s+termais|thermes?|terme",
  library: "bibliotecas?|librar(?:y|ies)|bibliotheques?|bibliothek\\w*",
  winery: "adegas?|winer(?:y|ies)|quintas?\\s+vinicolas?|vinicolas?|bodegas?|cantine\\s+vinicole|weingut\\w*",
  theme_park: "parques?\\s+(?:tematicos?|de\\s+diversoes?)|theme\\s+parks?|amusement\\s+parks?|freizeitpark\\w*",
};

/**
 * Fragmentos de negação por idioma. O grupo capturado é o resto da frase
 * onde se procura o termo de categoria (janela curta, evita falsos positivos
 * a meio de frases longas).
 */
const NEGATION_LEAD_PATTERNS: RegExp[] = [
  // Português
  /\bnao\s+quero\s+([a-z\s]{0,40})/g,
  /\bnao\s+incluir?\s+([a-z\s]{0,40})/g,
  /\bnao\s+queremos\s+([a-z\s]{0,40})/g,
  /\bsem\s+([a-z\s]{0,40})/g,
  /\bexcluir?\s+([a-z\s]{0,40})/g,
  /\bnada\s+de\s+([a-z\s]{0,40})/g,
  /\bnao\s+preciso\s+(?:de\s+)?(?:sugestoes\s+(?:de\s+)?)?([a-z\s]{0,40})/g,
  /\bdispenso\s+(?:as\s+|os\s+)?([a-z\s]{0,40})/g,
  /\bevit(?:a|e|ar|em)\s+(?:as\s+|os\s+|de\s+)?([a-z\s]{0,40})/g,
  /\bnao\s+gosto\s+de\s+([a-z\s]{0,40})/g,
  /\bnao\s+ha\s+interesse\s+(?:em\s+|por\s+)?([a-z\s]{0,40})/g,
  // English
  /\bdo\s?n'?t\s+want\s+(?:any\s+)?([a-z\s]{0,40})/g,
  /\bwithout\s+([a-z\s]{0,40})/g,
  // "no museums": o "no" isolado só nega o termo imediatamente a seguir —
  // caso contrário apanharia o "no"/"na" preposicional de pt/es ("no Algarve").
  /\bno\s+((?:more\s+|other\s+)?[a-z]+(?:\s+[a-z]+)?)\b/g,
  /\bexclude\s+([a-z\s]{0,40})/g,
  /\bskip\s+(?:the\s+)?([a-z\s]{0,40})/g,
  /\bavoid\s+(?:any\s+|the\s+)?([a-z\s]{0,40})/g,
  /\bdo\s?n'?t\s+need\s+(?:any\s+)?(?:suggestions\s+(?:for\s+)?)?([a-z\s]{0,40})/g,
  // Español
  /\bno\s+quiero\s+([a-z\s]{0,40})/g,
  /\bsin\s+([a-z\s]{0,40})/g,
  /\bno\s+necesito\s+([a-z\s]{0,40})/g,
  /\bexcluir\s+([a-z\s]{0,40})/g,
  // Français
  /\bje\s+ne\s+veux\s+pas\s+de\s+([a-z\s]{0,40})/g,
  /\bsans\s+([a-z\s]{0,40})/g,
  /\bje\s+n'?ai\s+pas\s+besoin\s+de\s+([a-z\s]{0,40})/g,
  /\bpas\s+de\s+([a-z\s]{0,40})/g,
  /\bevit(?:e|ez|er)\s+(?:les\s+|de\s+)?([a-z\s]{0,40})/g,
  // Italiano
  /\bnon\s+voglio\s+([a-z\s]{0,40})/g,
  /\bsenza\s+([a-z\s]{0,40})/g,
  /\bnon\s+ho\s+bisogno\s+di\s+([a-z\s]{0,40})/g,
  /\bevit(?:a|are|ate)\s+(?:i\s+|le\s+|di\s+)?([a-z\s]{0,40})/g,
  // Deutsch
  /\bich\s+moechte\s+keine\s+([a-z\s]{0,40})/g,
  /\bkeine\s+([a-z\s]{0,40})/g,
  /\bbrauche\s+keine\s+([a-z\s]{0,40})/g,
  /\bohne\s+([a-z\s]{0,40})/g,
  /\bvermeide\w*\s+(?:die\s+)?([a-z\s]{0,40})/g,
];

export function parseNegativePreferences(text: string): NegativePreferences {
  const folded = fold(text ?? "");
  const excluded = new Set<ExcludedCategoryKind>();
  const matchedPhrases: string[] = [];

  for (const leadRe of NEGATION_LEAD_PATTERNS) {
    leadRe.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = leadRe.exec(folded))) {
      const window = m[1] ?? "";
      if (!window.trim()) continue;
      for (const kind of Object.keys(CATEGORY_TERMS) as ExcludedCategoryKind[]) {
        const termRe = new RegExp(`\\b(${CATEGORY_TERMS[kind]})\\b`);
        if (termRe.test(window)) {
          excluded.add(kind);
          matchedPhrases.push(m[0].trim());
        }
      }
    }
  }

  return { excludedCategories: Array.from(excluded), matchedPhrases };
}

export function isCategoryExcluded(
  prefs: NegativePreferences | null | undefined,
  kind: ExcludedCategoryKind | string,
): boolean {
  if (!prefs) return false;
  return prefs.excludedCategories.includes(kind as ExcludedCategoryKind);
}

/**
 * Remove itens cuja categoria (obtida via `categoryOf`) esteja excluída.
 * Utilitário reutilizável para filtrar sugestões, POIs ou paragens já
 * geradas antes de as apresentar ou anexar ao roteiro.
 */
export function filterExcludedItems<T>(
  items: readonly T[],
  prefs: NegativePreferences | null | undefined,
  categoryOf: (item: T) => string | undefined | null,
): T[] {
  if (!prefs || prefs.excludedCategories.length === 0) return items.slice();
  return items.filter((item) => {
    const category = categoryOf(item);
    if (!category) return true;
    return !isCategoryExcluded(prefs, category);
  });
}

export const NO_EXCLUSIONS: NegativePreferences = { excludedCategories: [], matchedPhrases: [] };

/**
 * Tenta adivinhar a categoria excluída de um texto livre (nome de paragem,
 * categoria, tipo). Reutiliza os mesmos termos da deteção de exclusões, para
 * que o filtro final saiba reconhecer "Tasca do Zé" como restaurante mesmo
 * sem um campo de categoria estruturado.
 */
export function guessCategoryFromText(
  text: string | null | undefined,
): ExcludedCategoryKind | null {
  if (!text) return null;
  const folded = fold(text);
  for (const kind of Object.keys(CATEGORY_TERMS) as ExcludedCategoryKind[]) {
    const re = new RegExp(`\\b(${CATEGORY_TERMS[kind]})\\b`);
    if (re.test(folded)) return kind;
  }
  return null;
}

/**
 * Passe final de reparação: remove/neutraliza qualquer item cuja categoria
 * esteja excluída. Itens "tipo refeição" (ex.: pausa de almoço/jantar) não
 * desaparecem — tornam-se uma pausa neutra via `toNeutralBreak`. Todos os
 * outros itens excluídos são simplesmente descartados. As exclusões GANHAM
 * sempre sobre interesses automáticos, preenchimento e resultado da IA.
 */
export function repairExcluded<T>(
  items: readonly T[],
  prefs: NegativePreferences | null | undefined,
  opts: {
    categoryOf: (item: T) => string | undefined | null;
    isMealLike?: (item: T) => boolean;
    toNeutralBreak?: (item: T) => T;
  },
): T[] {
  if (!prefs || prefs.excludedCategories.length === 0) return items.slice();
  const out: T[] = [];
  for (const item of items) {
    const category = opts.categoryOf(item);
    const excluded = !!category && isCategoryExcluded(prefs, category);
    if (!excluded) {
      out.push(item);
      continue;
    }
    if (opts.isMealLike?.(item) && opts.toNeutralBreak) {
      out.push(opts.toNeutralBreak(item));
    }
    // Caso contrário: descarta definitivamente o item excluído.
  }
  return out;
}

/**
 * Verificação final (fail-fast) usada em testes e gates de reparação: nenhum
 * item da lista pode pertencer a uma categoria excluída.
 */
export function assertNoExcluded<T>(
  items: readonly T[],
  prefs: NegativePreferences | null | undefined,
  categoryOf: (item: T) => string | undefined | null,
): void {
  if (!prefs || prefs.excludedCategories.length === 0) return;
  for (const item of items) {
    const category = categoryOf(item);
    if (category && isCategoryExcluded(prefs, category)) {
      throw new Error(`assertNoExcluded: excluded category "${category}" leaked into output`);
    }
  }
}
